import { useState, useEffect, useCallback } from "react"
import { Upload, Trash2, ArrowUp, ArrowDown, Loader2, Image as ImageIcon } from "lucide-react"
import { toast } from "sonner"
import * as settingsService from "@/services/settingsService"
import { Button } from "../ui/button"

interface BannerItem {
  id: string; // Temp local ID or actual URL
  url: string; // Image preview URL (local blob URL or server URL)
  file?: File; // Store File object if newly uploaded
}

export function BannerManager() {
  const [banners, setBanners] = useState<BannerItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // API base URL for resolving relative image paths
  const API_HOST = import.meta.env.VITE_API_URL 
    ? import.meta.env.VITE_API_URL.replace("/api/v1", "") 
    : "http://localhost:3001"

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true)
      const urls = await settingsService.getBanners()
      setBanners(
        urls.map((url, idx) => ({
          id: `existing_${idx}`,
          url: url.startsWith("http") ? url : `${API_HOST}${url}`,
        }))
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error("Không thể tải danh sách banner: " + message)
    } finally {
      setLoading(false)
    }
  }, [API_HOST])

  useEffect(() => {
    fetchBanners()
  }, [fetchBanners])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const filesArray = Array.from(e.target.files)

    const remainingSlots = 5 - banners.length
    if (filesArray.length > remainingSlots) {
      toast.warning(`Chỉ được tải lên tối đa 5 ảnh. Bạn còn ${remainingSlots} lượt tải.`)
    }

    const filesToUpload = filesArray.slice(0, remainingSlots)

    const newItems: BannerItem[] = filesToUpload.map((file, idx) => {
      const localUrl = URL.createObjectURL(file)
      return {
        id: `new_${Date.now()}_${idx}`,
        url: localUrl,
        file: file,
      }
    })

    setBanners((prev) => [...prev, ...newItems])
    // Clear input
    e.target.value = ""
  }

  const handleDelete = (id: string) => {
    setBanners((prev) => {
      const target = prev.find((item) => item.id === id)
      // Revoke temporary blob URL if newly uploaded
      if (target?.url.startsWith("blob:")) {
        URL.revokeObjectURL(target.url)
      }
      return prev.filter((item) => item.id !== id)
    })
  }

  const moveItem = (index: number, direction: "up" | "down") => {
    const nextIndex = direction === "up" ? index - 1 : index + 1
    if (nextIndex < 0 || nextIndex >= banners.length) return

    setBanners((prev) => {
      const copy = [...prev]
      const temp = copy[index]
      copy[index] = copy[nextIndex]
      copy[nextIndex] = temp
      return copy
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const formData = new FormData()

      // Layout array will contain original URLs or reference keys to files
      const existingLayout: string[] = []

      banners.forEach((item, idx) => {
        if (item.file) {
          const fileKey = `file_${idx}`
          formData.append(fileKey, item.file)
          existingLayout.push(fileKey)
        } else {
          // Relativize absolute URL for saving to DB
          const relativeUrl = item.url.replace(API_HOST, "")
          existingLayout.push(relativeUrl)
        }
      })

      formData.append("existing", JSON.stringify(existingLayout))

      await settingsService.updateBanners(formData)
      toast.success("Cập nhật danh sách ảnh banner trang chủ thành công!")
      fetchBanners() // Reload to sync state and get clean server URLs
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error("Cập nhật thất bại: " + message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-border bg-card p-5">
        <Loader2 className="size-8 animate-spin text-primary" />
        <span className="ml-3 text-sm text-muted-foreground">Đang tải danh sách banner...</span>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5 mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <ImageIcon className="size-5 text-primary" />
            Cấu hình Banner trang chủ
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Quản lý tối đa 5 hình ảnh chạy dạng slider ở đầu trang chủ khách hàng.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {banners.length < 5 && (
            <label className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground cursor-pointer hover:bg-accent/80 transition-all active:scale-[0.98]">
              <Upload className="size-4" />
              Tải ảnh lên
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2 bg-primary text-primary-foreground hover:bg-primary/95 transition-all shadow-sm font-semibold active:scale-[0.98]"
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Đang lưu...
              </>
            ) : (
              "Lưu cấu hình"
            )}
          </Button>
        </div>
      </div>

      {banners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 rounded-lg border border-dashed border-border bg-accent/20">
          <ImageIcon className="size-12 text-muted-foreground/60 stroke-[1.2]" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Chưa có banner nào</h3>
          <p className="mt-1 text-center text-sm text-muted-foreground max-w-sm">
            Tải lên từ 1 đến 5 ảnh banner để chạy slider trang chủ. Nếu không có banner nào, hệ thống sẽ sử dụng ảnh mặc định của Homestay.
          </p>
          <label className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground cursor-pointer hover:bg-primary/95 transition-all shadow-sm active:scale-[0.98]">
            <Upload className="size-4" />
            Tải ảnh đầu tiên
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4">
            {banners.map((item, idx) => (
              <div
                key={item.id}
                className="group flex flex-col md:flex-row items-center gap-4 p-4 rounded-xl border border-border bg-accent/10 hover:bg-accent/25 transition-all"
              >
                {/* Image Preview Container */}
                <div className="relative aspect-[21/9] md:aspect-[16/7] w-full md:w-56 shrink-0 rounded-lg overflow-hidden border border-border shadow-inner bg-card">
                  <img
                    src={item.url}
                    alt={`Banner ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-0.5 rounded shadow-sm">
                    {idx + 1}
                  </div>
                </div>

                {/* Info and Actions */}
                <div className="flex-1 min-w-0 text-center md:text-left">
                  <h4 className="text-sm font-bold text-foreground truncate">
                    {item.file ? item.file.name : `Banner ảnh #${idx + 1}`}
                  </h4>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.file 
                      ? `${(item.file.size / 1024 / 1024).toFixed(2)} MB • Mới tải lên` 
                      : "Đã lưu trên hệ thống"
                    }
                  </p>
                </div>

                {/* Control Action Buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => moveItem(idx, "up")}
                    disabled={idx === 0}
                    className="size-9 border-border bg-card text-muted-foreground hover:text-foreground active:scale-95 transition-all"
                    title="Di chuyển lên"
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => moveItem(idx, "down")}
                    disabled={idx === banners.length - 1}
                    className="size-9 border-border bg-card text-muted-foreground hover:text-foreground active:scale-95 transition-all"
                    title="Di chuyển xuống"
                  >
                    <ArrowDown className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleDelete(item.id)}
                    className="size-9 border-destructive/20 bg-card text-destructive hover:bg-destructive/10 hover:text-destructive active:scale-95 transition-all"
                    title="Xóa banner này"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2 text-xs text-muted-foreground flex justify-between items-center px-1">
            <span>Sử dụng các nút di chuyển để sắp xếp thứ tự chạy ảnh của Slider.</span>
            <span>Tối đa: {banners.length}/5 ảnh</span>
          </div>
        </div>
      )}
    </div>
  )
}
